import { NextUIProvider } from '@nextui-org/react'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import React, { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { CampaignSiteLayout } from '~/components/campaign/CampaignSiteLayout'
import { CurieSelect } from '~/components/common/CurieSelect'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { SampleDataGeneratorForm } from '~/components/task-forms/SampleDataGeneratorForm'
import { AgentRequest, CommandType, SourceOutputExampleType } from '~/protos/agents'
import { trace } from '~/services/analyticsService'
import { localStoragePersister as persister, queryClient } from '~/services/queryClient'
import store from '~/store'
import { BetterToast } from '~/utils/toast'

const examples = [
  {
    label: 'Order Processing',
    key: '0',
    script: `
%dw 2.0
output application/json
---
{
  orderId: payload.orderId,
  totalCost: payload.items map ((item) -> {
    itemId: item.itemId,
    totalItemCost: vars.pricing[item.itemId] * item.quantity
  }),
  transactionDetails: {
    date: attributes.transactionDate,
    customer: attributes.customerName
  }
}
    `.trim()
  },
  {
    label: 'Customer Details',
    key: '1',
    script: `
%dw 2.0
output application/json
---
{
    address1: payload.order.buyer.address,
    city: payload.order.buyer.city,
    country: payload.order.buyer.nationality,
    email: payload.order.buyer.email,
    name: payload.order.buyer.name,
    postalCode: payload.order.buyer.postCode,
    stateOrProvince: payload.order.buyer.state
}
    `.trim()
  }
]

const Page: React.FC = () => {
  const [request, setRequest] = useState<AgentRequest>(AgentRequest.fromPartial({}))
  const [example, setExample] = useState<string[]>(['0'])
  const [defaultExampleType, setDefaultExampleType] = useState<SourceOutputExampleType>(
    SourceOutputExampleType.DATA_TYPE_JSON
  )
  const [defaultDWL, setDefaultDWL] = useState<string | undefined>(examples[0].script)

  const checkFieldsFilled = (request: AgentRequest) => {
    const commandStr = request.command?.commandString
    if (commandStr == null || commandStr.length === 0) {
      BetterToast.error('Please provide your DataWeave script')
      trace('campaign_task_fields_not_filled')
      return false
    }
    return true
  }

  const checkIfExampleRequest = (request: AgentRequest) => {
    const sourceData = request.command?.commandString
    return examples.some(example => example.script.trim() === sourceData?.trim())
  }

  return (
    <CampaignSiteLayout
      title='Sample Data Generator'
      description='Use Generative AI to generate the sample input data from DataWeave script'
      command={CommandType.DATAWEAVE_TO_DATA_CREATION}
      checkFieldsFilled={checkFieldsFilled}
      checkIfExampleRequest={checkIfExampleRequest}
      request={request}
      instructionsTitle='How to use this tool'
      instructions={
        <ol className='pl-2'>
          <li className='list-decimal list-inside'>
            Start by picking one of the examples below, hit generate and watch Curie create sample
            data for you.
          </li>
          <li className='list-decimal list-inside'>
            Edit the DataWeave script or provide your own, hit generate and watch Curie create
            custom sample data.
          </li>
        </ol>
      }
      examples={
        <div className='w-full flex mt-4 mb-8'>
          <div className='display flex items-center text-sm text-default-500'>
            <span className='mr-2'>Examples:</span>
            <CurieSelect
              placeholder='Select an example'
              className='w-48 pb-0'
              selectedKeys={example}
              items={examples.map(ex => ({ label: ex.label, key: ex.key }))}
              onSelectionChange={selectedKeys => {
                if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                  return
                }
                const key = selectedKeys.values().next().value as string
                setExample([key])
                const selectedExample = examples.find(ex => ex.key === key)
                if (selectedExample) {
                  setDefaultExampleType(SourceOutputExampleType.DATA_TYPE_JSON)
                  setDefaultDWL(selectedExample.script)
                }
              }}
            />
          </div>
        </div>
      }
    >
      <SampleDataGeneratorForm
        onChange={(task: TaskParams) => setRequest(task.request)}
        key={String(defaultDWL) + String(defaultExampleType)}
        dataweaveDefaultValue={defaultDWL}
        exampleTypeDefaultValue={defaultExampleType}
      />
    </CampaignSiteLayout>
  )
}

export default Page
